import axiosClient from '../axiosClient';
import { WorkspaceMemberRole } from '../../types/common';

export interface CreateInvitationDto {
  email: string;
  role: WorkspaceMemberRole;
  // Also add the invitee to this board (must belong to the target
  // workspace) once the invite is accepted. Omit for a workspace-only invite.
  boardId?: string;
}

export interface CreateInvitationResponse {
  id: string;
  email: string;
  role: WorkspaceMemberRole;
  boardId?: string | null;
  status: 'pending' | 'accepted' | 'expired';
  expiresAt: string;
}

export interface InvitationPreview {
  email: string;
  workspaceName: string;
  role: WorkspaceMemberRole;
  boardId?: string | null;
  boardName?: string | null;
  userExists: boolean;
}

export interface AcceptInvitationDto {
  fullName?: string;
  userName?: string;
  password?: string;
}

export interface AcceptInvitationResponse {
  workspaceId: string;
  workspaceName: string;
  boardId?: string | null;
  boardName?: string | null;
  email: string;
  // Only present when the invite was accepted by creating a brand new account
  token?: string;
}

export const invitationsApi = {
  // Invite someone to a workspace by email - sends them a link, does not add
  // them as a member directly (unlike the legacy /workspaces/:id/members).
  createInvitation: async (
    workspaceId: string,
    data: CreateInvitationDto
  ): Promise<CreateInvitationResponse> => {
    const payload: Record<string, unknown> = { email: data.email, role: data.role };
    if (data.boardId) {
      payload.boardId = Number(data.boardId);
    }
    const response = await axiosClient.post(`/workspaces/${workspaceId}/invitations`, payload);
    return response.data;
  },

  // Look up an invitation by its token, before the user decides to accept it
  getInvitation: async (token: string): Promise<InvitationPreview> => {
    const response = await axiosClient.get(`/invitations/${token}`);
    return response.data;
  },

  // Accept an invitation. If the caller is authenticated (axiosClient attaches
  // the bearer token automatically), it joins that account to the workspace.
  // If not, `data` must contain fullName/userName/password to create a new
  // account in the same request.
  acceptInvitation: async (
    token: string,
    data: AcceptInvitationDto
  ): Promise<AcceptInvitationResponse> => {
    const response = await axiosClient.post(`/invitations/${token}/accept`, data);
    return response.data;
  },
};
